import { type ReactNode } from "react";
import moment from "moment";
import { makeStyles } from "tss-react/mui";
import {
  MiAutocomplete,
  MiButton,
  MiDateRangePicker,
  MiListGroupedCard,
  MiLoader,
  MiPlaceholderArea,
} from "@miplatform/components";
import { IActivity } from "@miplatform/types";
import {
  groupActivitiesByDate,
  groupActivitiesForTimeline,
} from "@miplatform/utils";
import { mdiCalendarRefresh } from "@mdi/js";
import { Box, Typography } from "@mui/material";
import { ActivityCard } from "../ActivityCard/ActivityCard";
import { ActivityTimeline } from "../ActivityTimeline/ActivityTimeline";

export interface ActivityDateRange {
  startDate: Date | null;
  endDate: Date | null;
}

interface MiActivityLogFilterOption {
  label: string;
  value: string;
}

interface MiActivityLogProps {
  activities: IActivity[];
  isLoading?: boolean;
  showDateRangePicker?: boolean;
  dateRange?: ActivityDateRange;
  onDateRangeChange?: (range: ActivityDateRange) => void;
  dateFields?: string | string[];
  renderDetail?: (activity: IActivity) => ReactNode;
  showOrderTemplate?: boolean;
  onActivityClick?: (activity: IActivity) => void;
  showActivityCardBorder?: boolean;
  showPriceLevelFilter?: boolean;
  priceLevelOptions?: MiActivityLogFilterOption[];
  selectedPriceLevelId?: string;
  onPriceLevelChange?: (priceLevelId?: string) => void;
}

export function MiActivityLog({
  activities,
  isLoading = false,
  showDateRangePicker = false,
  dateRange,
  onDateRangeChange,
  dateFields = "createdOn",
  renderDetail,
  showOrderTemplate = false,
  onActivityClick,
  showActivityCardBorder = false,
  showPriceLevelFilter = false,
  priceLevelOptions = [],
  selectedPriceLevelId,
  onPriceLevelChange,
}: Readonly<MiActivityLogProps>) {
  const { classes, theme } = useStyles();

  const handleDateChange = (
    start: ReturnType<typeof moment> | null,
    end: ReturnType<typeof moment> | null
  ) => {
    onDateRangeChange?.({
      startDate: start ? start.toDate() : null,
      endDate: end ? end.toDate() : null,
    });
  };

  const handleReset = () => {
    onDateRangeChange?.({ startDate: null, endDate: null });
  };

  return (
    <Box className={classes.root}>
      {showDateRangePicker && (
        <Box className={classes.dateRangeBar}>
          <MiDateRangePicker
            dateRange={dateRange}
            onDateChange={handleDateChange}
            showThisWeekButton={false}
            showShortCutItems
            calendarCount={1}
            placeholder="Select your date range"
            fontSize="18px"
          />
          <MiButton
            title="Reset"
            startIcon={{
              path: mdiCalendarRefresh,
              iconColor: theme.palette.grey.main,
            }}
            onClick={handleReset}
            variant="outlined"
            disabled={!dateRange?.startDate}
          />
        </Box>
      )}

      {showPriceLevelFilter && (
        <Box className={classes.priceLevelFilterBar}>
          <MiAutocomplete
            label="Price Level"
            options={priceLevelOptions}
            value={priceLevelOptions.find((option) => option.value === selectedPriceLevelId) ?? null}
            onChange={(_e, value) => onPriceLevelChange?.(value?.value)}
          />
        </Box>
      )}

      {isLoading && (
        <Box display="flex" justifyContent="center" pt={4}>
          <MiLoader isLoading />
        </Box>
      )}

      {!isLoading && activities.length === 0 && (
        <MiPlaceholderArea
          text={
            dateRange?.startDate
              ? "No activity found for the selected date range"
              : "No Activity to Display"
          }
          containerStyle={{
            minHeight: 200,
            backgroundColor: theme.palette.grey.light,
          }}
        />
      )}

      {!isLoading && activities.length > 0 && (
        <Box className={classes.listContainer}>
          {Object.entries(groupActivitiesByDate(activities, dateFields)).map(
            ([date, dateActivities]) => {
              const timelineData = {
                [date]: groupActivitiesForTimeline(dateActivities, dateFields),
              };

              return (
                <MiListGroupedCard
                  key={date}
                  title={
                    (
                      <Typography
                        variant="body2"
                        color="text.primary"
                        fontWeight={theme.fontWeight.semiBold}
                        fontSize={theme.fontSize.medium}
                        lineHeight={1}
                      >
                        {date}
                      </Typography>
                    ) as unknown as string
                  }
                  renderCard={
                    <ActivityTimeline
                      disableSpacing
                      data={timelineData}
                      showHeader={false}
                      showDate={false}
                      renderDetail={
                        renderDetail ??
                        ((activity: IActivity) => (
                          <ActivityCard
                            key={activity.id}
                            activity={activity}
                            showOrderTemplate={showOrderTemplate}
                            onClick={onActivityClick}
                            showCardBorder={
                              showActivityCardBorder || Boolean(onActivityClick)
                            }
                          />
                        ))
                      }
                    />
                  }
                />
              );
            }
          )}
        </Box>
      )}
    </Box>
  );
}

const useStyles = makeStyles()((theme) => ({
  root: {
    display: "flex",
    flexDirection: "column",
    width: "100%",
    height: "100%",
  },
  dateRangeBar: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: theme.spacing(2),
    padding: theme.spacing(1.5, 2),
  },
  priceLevelFilterBar: {
    padding: theme.spacing(1.5, 2),
  },
  listContainer: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1),
    padding: theme.spacing(2),
    overflow: "auto",
    flex: 1,
  },
}));
